import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import { useRef, useState, useEffect } from "react";
import "./index.css";
import AutocompleteArrow from "../AutocompleteDropdownArrow";

const EditableResultDropdownAutocomplete = ({
  chosen,
  classStyle = "newreact__select__azilis",
  setValue,
  disabled = false,
  propertyName = "value",
  styleNo = 1,
  scrollLeft = 0,
  menu,
  edit,
  options,
  check,
}) => {
  const divRef = useRef();

  const safeOptions = Array.isArray(options) ? options : [];

  const groupedOptions = safeOptions.reduce((acc, option) => {
    const category = option.category ? option.category : option.subgroup_en;
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(option);
    return acc;
  }, {});

  const flattenedOptions = Object.values(groupedOptions).reduce(
    (acc, options) => {
      return [...acc, ...options];
    },
    []
  );

  // Find matching option if chosen is a string (code from API) or ensure object structure is correct
  const getMatchingOption = (chosenValue) => {
    if (!chosenValue) return null;

    // If it's a string, find matching option
    if (typeof chosenValue === "string") {
      return (
        flattenedOptions.find(
          (opt) =>
            opt.value === chosenValue ||
            opt.title === chosenValue ||
            opt.name === chosenValue
        ) || null
      );
    }

    // If it's an object, ensure it has the right structure and find matching option from our list
    if (typeof chosenValue === "object") {
      // Try to find the matching option by value (code) to ensure we have the correct reference
      if (chosenValue.value) {
        const matched = flattenedOptions.find(
          (opt) => opt.value === chosenValue.value
        );
        if (matched) return matched;
      }
      // If no match by value, check if the object structure matches our options
      const matchedByTitle = flattenedOptions.find(
        (opt) =>
          opt.title === chosenValue.title || opt.name === chosenValue.name
      );
      if (matchedByTitle) return matchedByTitle;

      // Return as-is if it has the expected structure
      if (chosenValue.title || chosenValue.name) {
        return chosenValue;
      }
    }

    return null;
  };

  const [expandedGroups, setExpandedGroups] = useState(() => new Set());

  // While options are still fetching, `options` is often []. Without this, MUI shows "No options"
  // which looks like a bug. After a timeout, assume the list is genuinely empty.
  const [emptyListLooksFinal, setEmptyListLooksFinal] = useState(false);
  useEffect(() => {
    if (flattenedOptions.length > 0) {
      setEmptyListLooksFinal(false);
      return;
    }
    const t = setTimeout(() => setEmptyListLooksFinal(true), 20000);
    return () => clearTimeout(t);
  }, [flattenedOptions.length]);

  const optionsStillLoading =
    flattenedOptions.length === 0 && !emptyListLooksFinal;

  const toggleGroup = (groupKey, e) => {
    e.preventDefault();
    e.stopPropagation();
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(groupKey)) next.delete(groupKey);
      else next.add(groupKey);
      return next;
    });
  };

  const renderGroup = (params) => {
    const isExpanded = expandedGroups.has(params.group);
    return (
      <li key={params.key}>
        <div
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              toggleGroup(params.group, e);
            }
          }}
          onClick={(e) => toggleGroup(params.group, e)}
          style={{
            fontWeight: "600",
            cursor: "pointer",
            padding: "6px 12px",
            userSelect: "none",
            display: "flex",
            alignItems: "center",
            gap: "6px",
          }}
        >
          <span style={{ fontSize: "10px" }}>{isExpanded ? "▼" : "▶"}</span>
          {params.group}
        </div>
        {isExpanded && (
          <ul style={{ width: "200px", listStyle: "none", paddingLeft: 0 }}>
            {params.children}
          </ul>
        )}
      </li>
    );
  };

  return (
    <>
      {!edit ? (
        check === "noProperty" ? (
          <h4>{chosen}</h4>
        ) : (
          <h4>{chosen ? chosen[propertyName] : ""}</h4>
        )
      ) : (
        <div
          className="w-100 editable-result-dropdown-category"
          ref={divRef}
        >
          <Autocomplete
            className={classStyle + " azilis__dropdown__style" + styleNo}
            classNamePrefix="select__prefix__azilis"
            loading={optionsStillLoading}
            loadingText="Loading options…"
            onChange={(event, opt) => {
              setValue(opt);
            }}
            value={
              check === "noProperty"
                ? (() => {
                    const matched = getMatchingOption(chosen);
                    // Ensure we return null if no match, so Autocomplete can handle it properly
                    return matched;
                  })()
                : chosen
                ? chosen[propertyName]
                : null
            }
            isSearchable
            disabled={disabled}
            filterOptions={(opts, { inputValue }) => {
              if (
                inputValue == null ||
                typeof inputValue !== "string" ||
                !inputValue.trim()
              ) {
                return opts;
              }
              const searchValue = inputValue.trim().toLowerCase();
              return opts.filter((option) => {
                if (!option) return false;
                const label = option.title || option;
                if (!label || typeof label !== "string") return false;
                return label.toLowerCase().includes(searchValue);
              });
            }}
            noOptionsText={
              flattenedOptions.length === 0
                ? emptyListLooksFinal
                  ? "No options available"
                  : "Loading options…"
                : "No results"
            }
            ListboxProps={{
              sx: { maxHeight: 360, minHeight: optionsStillLoading ? 48 : undefined },
            }}
            options={flattenedOptions}
            groupBy={(option) =>
              option.category ? option.category : option.subgroup_en
            }
            getOptionLabel={(option) => {
              if (!option) return "";
              if (typeof option === "string") {
                // If it's a string (code), try to find the matching option to get the title
                const matchedOption = flattenedOptions.find(
                  (opt) =>
                    opt.value === option ||
                    opt.title === option ||
                    opt.name === option
                );
                return matchedOption?.title || option;
              }
              if (typeof option === "object") {
                return option.title || option.name || option.value || "";
              }
              return String(option);
            }}
            isOptionEqualToValue={(option, value) => {
              if (!value || !option) return false;

              // Handle string values (code from API)
              if (typeof value === "string") {
                return (
                  option.value === value ||
                  option.title === value ||
                  option.name === value
                );
              }

              // Handle object values (from dropdown selection)
              if (typeof value === "object") {
                // Match by value (code) first, then by title (name)
                if (value.value && option.value) {
                  return option.value === value.value;
                }
                if (value.title && option.title) {
                  return option.title === value.title;
                }
                if (value.name && option.name) {
                  return option.name === value.name;
                }
                // Fallback: compare the objects directly
                return option === value;
              }

              return false;
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                InputProps={{
                  ...params.InputProps,
                  sx: {
                    fontSize: "12px",
                    color: "#ffffff",
                    "& .MuiInputBase-input": {
                      color: "#ffffff",
                    },
                    "& input": {
                      color: "#ffffff",
                    },
                    "& input::placeholder": {
                      color: "rgba(255, 255, 255, 0.65)",
                    },
                  },
                }}
                InputLabelProps={{
                  sx: {
                    fontSize: "12px",
                  },
                }}
              />
            )}
            renderGroup={renderGroup}
            renderOption={(props, option, { selected }) => (
              <li {...props}>{option.title}</li>
            )}
            classes={{
              paper: "dropdown-bg-custom",
            }}
            componentsProps={{
              paper: {
                sx: {
                  width: 300,
                },
              },
              popper: {
                className: "editable-result-dropdown-category__popper",
                placement: "bottom-start",
                style: { zIndex: 2000 },
                modifiers: [
                  {
                    name: "preventOverflow",
                    enabled: true,
                    options: {
                      altAxis: true,
                      tether: false,
                      padding: 8,
                      boundary: "viewport",
                    },
                  },
                ],
              },
              clearIndicator: {
                sx: {
                  color: "white",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                },
              },
            }}
            popupIcon={<AutocompleteArrow />}
            styles={{
              menu: (baseStyles, state) => ({
                ...baseStyles,
                left: divRef.current
                  ? divRef.current.offsetLeft - scrollLeft + "px"
                  : "unset",
              }),
            }}
            sx={{
              "& .MuiAutocomplete-inputRoot": {
                paddingRight: "0",
                color: "#ffffff",
                "& input": {
                  color: "#ffffff",
                },
              },
              "&.MuiAutocomplete-hasClearIcon .MuiOutlinedInput-root": {
                paddingRight: "0",
                color: "#ffffff",
                "& input": {
                  color: "#ffffff",
                },
              },
              "&.MuiAutocomplete-hasPopupIcon.MuiAutocomplete-hasClearIcon .MuiOutlinedInput-root":
                {
                  paddingRight: "18px",
                  color: "#ffffff",
                  "& input": {
                    color: "#ffffff",
                  },
                },
              "& input": {
                color: "#ffffff",
              },
              "& .MuiInputBase-input": {
                color: "#ffffff",
              },
            }}
          />
        </div>
      )}
    </>
  );
};

export default EditableResultDropdownAutocomplete;
